'use client'

/**
 * Chat comercial de pós-graduação (HN-3170).
 *
 * Substitui os botões de contato padrão (Fale Conosco / WhatsApp) nas
 * páginas de pós-graduação das vitrines (SóEducador, Estetus, Eduhot).
 * Renderiza `null` fora dessas páginas ou em plataformas sem o fluxo
 * configurado (RN02, RN04) — quem controla a ocultação dos widgets
 * padrão é `isPostgraduateChatRoute` (usado em `WhatsAppButton`,
 * `ChatwootConfig` e `AtaliaChatConfig`).
 */

import { MessageCircle } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { useUnity } from '@/hooks/use-unity'
import {
  dispatchPostgraduateChatOpen,
  usePostgraduateChat,
} from '@/hooks/use-postgraduate-chat'
import { buildGreetingMessages } from '@/lib/postgraduate-chat/chat-flow'
import { getPostgraduateChatConfig } from '@/lib/postgraduate-chat/config'
import { isPostgraduateChatRoute } from '@/lib/postgraduate-chat/route'
import { ChatPanel } from './chat-panel'

const AUTO_OPEN_DELAY_MS = 10_000
const AUTO_OPEN_STORAGE_KEY_PREFIX = 'zamply:postgraduate-chat:auto-open:v1:'
const LAUNCHER_SOURCE = 'Launcher do chat de pós'
const AUTO_OPEN_SOURCE = 'auto-10s'

interface PostgraduateChatProps {
  /** Curso da página de informações — ausente na página de listagem. */
  readonly courseId?: string
  readonly courseName?: string
  readonly courseAmount?: string | number | null
  /** Imagem do curso — vai no e-mail do programa completo. */
  readonly courseImage?: string | null
}

function hasAutoOpenFired(storageKey: string): boolean {
  if (typeof window === 'undefined') {
    return true
  }
  try {
    return window.sessionStorage.getItem(storageKey) === '1'
  } catch {
    return false
  }
}

function markAutoOpenFired(storageKey: string): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.sessionStorage.setItem(storageKey, '1')
  } catch {
    // Sem persistência — segue só nesta montagem.
  }
}

export function PostgraduateChat({
  courseId,
  courseName,
  courseAmount,
  courseImage,
}: PostgraduateChatProps) {
  const pathname = usePathname()
  const { unity, metadataDetails } = useUnity()

  const platformConfig = getPostgraduateChatConfig(unity?.reference)
  const shouldRender = isPostgraduateChatRoute(pathname) && Boolean(platformConfig)

  if (!(shouldRender && platformConfig)) {
    return null
  }

  return (
    <PostgraduateChatPanelHost
      brandName={unity?.full_name}
      agendorFunnel={metadataDetails?.agendorFunnel}
      courseAmount={courseAmount}
      courseId={courseId}
      courseImage={courseImage}
      courseName={courseName}
      originPage={pathname}
      platformConfig={platformConfig}
      unityReference={unity?.reference ?? undefined}
    />
  )
}

interface PostgraduateChatPanelHostProps {
  readonly platformConfig: NonNullable<ReturnType<typeof getPostgraduateChatConfig>>
  readonly unityReference?: string
  readonly brandName?: string | null
  readonly agendorFunnel?: Record<string, any> | null
  readonly courseId?: string
  readonly courseName?: string
  readonly courseAmount?: string | number | null
  readonly courseImage?: string | null
  readonly originPage?: string | null
}

function PostgraduateChatPanelHost({
  platformConfig,
  unityReference,
  brandName,
  agendorFunnel,
  courseId,
  courseName,
  courseAmount,
  courseImage,
  originPage,
}: PostgraduateChatPanelHostProps) {
  const storageKey = `zamply:postgraduate-chat:v1:${unityReference ?? 'default'}`
  const greetingMessages = buildGreetingMessages(
    `Olá, sou ${platformConfig.attendantPronoun} ${platformConfig.attendantName}.`
  )

  const { state, currentStep, answer, selectOption, start, retry, close, open } =
    usePostgraduateChat({
      storageKey,
      greetingMessages,
      context: {
        brandName,
        agendorFunnel,
        courseId,
        courseName,
        courseAmount,
        courseImage,
        originPage: originPage ?? undefined,
      },
    })

  const isOpen = state.phase !== 'fechado'
  const autoOpenStorageKey = `${AUTO_OPEN_STORAGE_KEY_PREFIX}${unityReference ?? 'default'}`
  const openRef = useRef(open)
  openRef.current = open

  // Abertura automática após 10s, uma única vez por sessão, nunca para
  // quem já concluiu o fluxo. Qualquer abertura manual (launcher ou botão
  // da página) também conta como "já disparou".
  useEffect(() => {
    if (state.phase !== 'fechado') {
      markAutoOpenFired(autoOpenStorageKey)
      return
    }
    if (state.completed || hasAutoOpenFired(autoOpenStorageKey)) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      markAutoOpenFired(autoOpenStorageKey)
      dispatchPostgraduateChatOpen(AUTO_OPEN_SOURCE)
    }, AUTO_OPEN_DELAY_MS)

    return () => window.clearTimeout(timeoutId)
  }, [state.completed, state.phase, autoOpenStorageKey])

  function openFromLauncher() {
    dispatchPostgraduateChatOpen(LAUNCHER_SOURCE)
  }

  if (!isOpen) {
    return (
      <button
        aria-label={`Abrir chat com ${platformConfig.attendantName}`}
        className="fixed right-6 bottom-6 z-40 flex size-14 items-center justify-center rounded-full bg-primary-900 text-white shadow-[0_20px_50px_rgba(12,34,143,0.25)] transition-transform hover:scale-105"
        onClick={openFromLauncher}
        type="button"
      >
        <MessageCircle className="size-6" />
      </button>
    )
  }

  return (
    <ChatPanel
      answer={answer}
      attendantName={platformConfig.attendantName}
      close={close}
      currentStep={currentStep}
      retry={retry}
      selectOption={selectOption}
      start={start}
      state={state}
      whatsappNumber={platformConfig.whatsappNumber}
    />
  )
}
